import * as React from 'react'

import { Popover as PopoverPrimitive } from '@base-ui/react/popover'

import { cn } from '~/client/src/shared/utils/cn'
import {
  isWithinAnchor,
  useCloseOnFocusLeavingAnchor,
} from '~/client/src/shared/utils/popoverAnchor'

const DEFAULT_SIDE = 'bottom' as const
const DEFAULT_ALIGN = 'start' as const
const DEFAULT_SIDE_OFFSET = 4

type PopoverSide = 'top' | 'bottom' | 'left' | 'right'
type PopoverAlign = 'start' | 'center' | 'end'

interface IProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Rendered inside the popover root; must contain a `PopoverTrigger` over a real button. */
  trigger: React.ReactNode
  /** The popup sits against this element, and a press inside it never dismisses the popup. */
  anchor: React.RefObject<HTMLElement>
  children: React.ReactNode
  side?: PopoverSide
  align?: PopoverAlign
  sideOffset?: number
  /** Portal target (e.g. the fullscreen element); undefined -> body default. */
  container?: HTMLElement
}

export const PopoverSurface: React.FC<IProps> = ({
  open,
  onOpenChange,
  trigger,
  anchor,
  children,
  side = DEFAULT_SIDE,
  align = DEFAULT_ALIGN,
  sideOffset = DEFAULT_SIDE_OFFSET,
  container,
}) => {
  const handleOpenChange = (
    nextOpen: boolean,
    details: PopoverPrimitive.Root.ChangeEventDetails,
  ): void => {
    if (!nextOpen && isWithinAnchor(details, anchor)) {
      return
    }
    onOpenChange(nextOpen)
  }
  const close = React.useCallback(() => onOpenChange(false), [onOpenChange])
  useCloseOnFocusLeavingAnchor(anchor, open, close)

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      {trigger}
      {/* container ?? undefined: base-ui treats container={null} as "no portal". */}
      <PopoverPrimitive.Portal container={container ?? undefined}>
        <PopoverPrimitive.Positioner
          anchor={anchor}
          side={side}
          align={align}
          sideOffset={sideOffset}
          // Pin side; let max-h cap to available space. Default flip snaps the popover
          // beside the pill (breaks the "under the pill" mental model).
          collisionAvoidance={{ side: 'none' }}
          className={cn('tw:isolate tw:z-50')}
        >
          <PopoverPrimitive.Popup
            data-slot="popover-content"
            className={cn(
              // max-h-(--available-height) caps to base-ui's size middleware result so long lists scroll inside.
              'tw:flex tw:w-[400px] tw:max-h-(--available-height) tw:origin-(--transform-origin) tw:flex-col tw:rounded-lg tw:bg-popover tw:text-popover-foreground tw:shadow-md tw:ring-1 tw:ring-foreground/10 tw:outline-hidden tw:overflow-hidden tw:duration-100 tw:data-open:animate-in tw:data-open:fade-in-0 tw:data-open:zoom-in-95 tw:data-closed:animate-out tw:data-closed:fade-out-0 tw:data-closed:zoom-out-95',
            )}
          >
            {children}
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
